import type { KyResponse } from 'ky';
import { useEffect, useMemo, useState } from 'react';
import type { PrivateKeyAccount } from 'viem/accounts';

// a type-only import: erased from the bundle, the module itself is only ever loaded by the `import()` below
import type * as V2BackendModuleExports from '@/features/browserWallet/backend/createV2Backend';
// deep imports (not the feature barrels): neither store reaches back into the wallet feature.
import { useBrowserWalletStore } from '@/features/browserWallet/store';
import { useFlowStore } from '@/features/flow/store';
import type { SpeedBackend } from '@/lib/api/speed';
import { ApiError } from '@/lib/errors';
import { loadEnginePresenter } from '@/lib/errors/engine';

type V2BackendModule = typeof V2BackendModuleExports;

let loaded: V2BackendModule | null = null;
let loading: Promise<V2BackendModule> | null = null;

/**
 * Loads the V2 backend module (the vendored engine) once per page, behind a dynamic `import()`, together with the
 * vendored error presenter the web presenter hands the engine's errors to (so it is in place before the first one).
 */
async function loadV2BackendModule(): Promise<V2BackendModule> {
    loading ??= Promise.all([
        import('@/features/browserWallet/backend/createV2Backend'),
        loadEnginePresenter(),
    ]).then(
        ([module]) => {
            loaded = module;
            return module;
        },
        (error: unknown) => {
            // a failed chunk load may succeed on the next attempt
            loading = null;
            throw error;
        },
    );
    return loading;
}

// One backend per wallet: the memoized reads and the exchange client live in it, so every hook shares them.
let current: {
    address: string;
    backend: SpeedBackend;
    /** False once the wallet is retired: the instance then refuses every call. */
    live: { value: boolean };
} | null = null;

const WALLET_CHANGED_MESSAGE =
    'Your Snappy Wallet changed in this browser. Try again.';

/** What a retired backend answers: a coded refusal like the V2 backend's own, plain for the user. */
function walletChangedError(): ApiError {
    // nothing went over the wire: a synthetic empty response, like the V2 backend's coded errors
    const response = new Response(null, {
        status: 409,
    }) as unknown as KyResponse;
    return new ApiError(
        WALLET_CHANGED_MESSAGE,
        409,
        'Conflict',
        { code: 'walletChanged', message: WALLET_CHANGED_MESSAGE },
        response,
    );
}

/**
 * The backend with every call gated on `live`: a closure that outlives a wallet change (a close's wait loop, a
 * mutation) holds the previous wallet's instance, and once that wallet is retired it can neither act nor read its
 * wallet's data into the caches the next wallet's hooks read. Every `SpeedBackend` method returns a promise.
 */
function retirable(
    backend: SpeedBackend,
    live: { value: boolean },
): SpeedBackend {
    const gated: Record<string, unknown> = {};
    for (const [name, value] of Object.entries(backend)) {
        gated[name] =
            typeof value === 'function'
                ? async (...args: unknown[]): Promise<unknown> => {
                      if (!live.value) {
                          throw walletChangedError();
                      }
                      return (value as (...a: unknown[]) => unknown).apply(
                          backend,
                          args,
                      );
                  }
                : value;
    }
    return gated as unknown as SpeedBackend;
}

function backendFor(
    module: V2BackendModule,
    address: `0x${string}`,
    account: PrivateKeyAccount,
): SpeedBackend {
    if (current?.address !== address.toLowerCase()) {
        const live = { value: true };
        current = {
            address: address.toLowerCase(),
            backend: retirable(
                module.createV2Backend({ address, account }),
                live,
            ),
            live,
        };
    }
    return current.backend;
}

/**
 * The browser wallet store retires `address` (a wallet change, a forget): its memoized backend is dropped and the
 * instance refuses every call from now on, so nothing still holding it can act for, or read, the previous wallet. The
 * next signer gets a fresh backend.
 */
export function forgetV2Backend(address: string): void {
    if (current?.address === address.toLowerCase()) {
        current.live.value = false;
        current = null;
    }
}

/** Tests only: forgets the loaded module and the memoized backend. */
export function resetV2BackendHookForTests(): void {
    loaded = null;
    loading = null;
    current = null;
}

/**
 * The V2 (browser wallet) branch of `useSpeedBackend`: the backend for the unlocked browser wallet, built on the
 * vendored engine. `null` while the active flow is not V2, while the wallet is locked (which includes the retire
 * window of a wallet change: the store publishes `signer: null` before the next wallet's key is written), and until
 * the backend module has loaded (it is fetched lazily, so a V1 session never loads the engine). One backend per
 * wallet address, and only ever the backend of the signer the store holds right now.
 */
export function useV2Backend(): SpeedBackend | null {
    const isV2 = useFlowStore((s) => s.active === 'v2');
    const signer = useBrowserWalletStore((s) => s.signer);
    const [module, setModule] = useState<V2BackendModule | null>(loaded);
    const wanted = isV2 && signer !== null;

    useEffect(() => {
        if (!wanted || module !== null) {
            return;
        }
        let live = true;
        loadV2BackendModule()
            .then((loadedModule) => {
                if (live) {
                    setModule(loadedModule);
                }
            })
            .catch((error: unknown) => {
                console.warn(
                    '[V2Backend] the backend module did not load',
                    error,
                );
            });
        return () => {
            live = false;
        };
    }, [wanted, module]);

    return useMemo(() => {
        if (!wanted || module === null || signer === null) {
            return null;
        }
        // never a backend for a wallet the store no longer holds (a render that raced a retire)
        const held = useBrowserWalletStore.getState().signer;
        if (
            held === null ||
            held.address.toLowerCase() !== signer.address.toLowerCase()
        ) {
            return null;
        }
        // the store keeps the account loosely typed; it is the viem account of the unlocked key
        const account = signer.account as PrivateKeyAccount;
        return backendFor(module, signer.address, account);
    }, [wanted, module, signer]);
}
